import React, { useContext, useState } from "react";
import { CorrectOrIncorrect, Question, QuizModuleId } from "@common";

import { StatValues } from "../interfaces/answerStats.interface";
import useCurrentSlideData from "../hooks/useCurrentSlideData";
import useQuizSlideInfo from "../hooks/useQuizSlideInfo";
import useSocket from "../hooks/useWebsocket";
import { addAnswerToStorage } from "../util/questionUtils";
import { SlideDataContext } from "./slideDataContext";
import { TestStoreContext } from "./testStoreContext";
import { WebsocketContext } from "./websocketContext";
import useCurrentQuizModule from "@natklok/hooks/useCurrentQuizModule";
import { useQuizModules } from "@natklok/hooks/useQuizModules";

interface SelectAnswerContextType {
  showingCorrectAnswer: boolean;
  onSelectAnswer: (
    correctOrIncorrect: CorrectOrIncorrect,
    statValues: StatValues,
    showAnswerDelay?: number
  ) => void;
  setShowingCorrectAnswer: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SelectAnswerContext = React.createContext<SelectAnswerContextType>({
  showingCorrectAnswer: false,
  onSelectAnswer: () => null,
  setShowingCorrectAnswer: (val) => null,
});

export const SelectAnswerProvider: React.FC<{
  children: React.ReactElement;
  question?: Question;
}> = ({ children, question }) => {
  const currentQuizModule: QuizModuleId = useCurrentQuizModule();
  const currentSlideData: any = useCurrentSlideData();
  const {
    confettiIsRunning,
    setConfettiIsRunning,
    thumbsDownIsRunning,
    setThumbsDownIsRunning,
    setStarAnimIsRunning,
  } = useContext(TestStoreContext);
  const [showingCorrectAnswer, setShowingCorrectAnswer] = useState(false);
  const { questionsReducer } = useContext(SlideDataContext);
  const { wsAnswerQuestion } = useSocket();
  const { currentSlideIndex } = useQuizSlideInfo();
  const { inMultiplayerSession, isMultiplayerHost } = useContext(WebsocketContext);
  const { data: quizModulesData } = useQuizModules();
  const quizModules = quizModulesData ?? {};

  // Update SlideDataContext, update web storage, show confetti, go to answer page and show correct info.
  const onSelectAnswer = (
    correctOrIncorrect: CorrectOrIncorrect,
    answerValues: StatValues,
    showAnswerDelay = 1500
  ) => {
    if (correctOrIncorrect === "incorrect") {
      showAnswerDelay *= 1.5;
    }

    // Host gets to look at the correct answer longer
    if (isMultiplayerHost) {
      showAnswerDelay = 4000;
    }

    setShowingCorrectAnswer(true);

    // Only show confetti only for questions
    if (question || currentSlideData.question !== undefined) {
      if (correctOrIncorrect === "correct") {
        setConfettiIsRunning(!confettiIsRunning);
        setStarAnimIsRunning(true);
      } else if (correctOrIncorrect === "incorrect") {
        setThumbsDownIsRunning(!thumbsDownIsRunning);
      }
    }

    setTimeout(() => {
      setShowingCorrectAnswer(false);

      questionsReducer({
        type: "changeQuestionState",
        index: currentSlideIndex,
        newState: correctOrIncorrect,
      });

      const currQuestion: Question | undefined = question || currentSlideData.question;
      if (currQuestion !== undefined) {
        addAnswerToStorage({
          correctOrIncorrect,
          question: currQuestion,
          quizModuleId: currentQuizModule,
          // TODO COMPLETE: Add quiz modules
          quizModules,
        });

        if (inMultiplayerSession) {
          wsAnswerQuestion(
            currQuestion.id.sv,
            correctOrIncorrect,
            answerValues,
            currentSlideData.question !== undefined
          );
        }
      }
    }, showAnswerDelay);
  };

  return (
    <SelectAnswerContext.Provider
      value={{ onSelectAnswer, showingCorrectAnswer, setShowingCorrectAnswer }}
    >
      {children}
    </SelectAnswerContext.Provider>
  );
};
